import { useState } from "react";
import { KeyRound, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { AppLayout } from "@/components/layout/AppLayout";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { useEnquirySources } from "@/modules/enquiries/hooks/useEnquiries";
import {
  useApiKeyList,
  useDeleteApiKey,
  useRevokeApiKey,
} from "../hooks/useApiKeys";
import { NewApiKeyDrawer } from "../components/NewApiKeyDrawer";
import { EditApiKeyDrawer } from "../components/EditApiKeyDrawer";
import { ApiKeyCreatedModal } from "../components/ApiKeyCreatedModal";
import type { ApiKeyCreated, ApiKeyItem } from "../types";

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string }> = {
    active: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)" },
    inactive: { bg: "hsl(38,92%,92%)", fg: "hsl(32,80%,34%)" },
    revoked: { bg: "hsl(0,80%,94%)", fg: "hsl(0,72%,42%)" },
  };
  const s = styles[status] ?? styles.inactive;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: s.bg, color: s.fg }}
    >
      {status}
    </span>
  );
}

export default function ApiKeysAdminPage() {
  const [sourceFilter, setSourceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);
  const [editTarget, setEditTarget] = useState<ApiKeyItem | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApiKeyItem | null>(null);

  const { data: sources = [] } = useEnquirySources();
  const { data, isLoading, isError } = useApiKeyList({
    source: sourceFilter || undefined,
    status: statusFilter || undefined,
  });
  const revoke = useRevokeApiKey();
  const del = useDeleteApiKey();

  const rows = data?.data ?? [];

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Administration" },
        { label: "API Keys" },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <KeyRound
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">API Keys</h1>
          </div>
          <Button onClick={() => setDrawerOpen(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            New API Key
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="w-44"
          >
            <option value="">All Sources</option>
            {sources.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-44"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="revoked">Revoked</option>
          </Select>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            Failed to load API keys (admin access required).
          </div>
        ) : (
          <div
            className="overflow-hidden rounded-xl border bg-card"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                Loading…
              </div>
            ) : rows.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                No API keys yet.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-6 py-3">Code</th>
                    <th className="px-6 py-3">Name</th>
                    <th className="px-6 py-3">Source</th>
                    <th className="px-6 py-3">Prefix</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Last Used</th>
                    <th className="px-6 py-3">Usage</th>
                    <th className="px-6 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((k) => (
                    <tr key={k.id} className="hover:bg-[hsl(210,50%,98%)]">
                      <td className="px-6 py-4 font-mono text-xs text-muted-foreground">
                        {k.code}
                      </td>
                      <td className="px-6 py-4 font-medium text-foreground">
                        {k.name}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {k.source}
                      </td>
                      <td className="px-6 py-4 font-mono text-xs text-muted-foreground">
                        {k.keyPrefix}…
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={k.status} />
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {k.lastUsedAt
                          ? new Date(k.lastUsedAt).toLocaleString("en-GB")
                          : "Never"}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {k.usageCount}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          {!k.revokedAt && (
                            <>
                              <button
                                onClick={() => setEditTarget(k)}
                                className="text-xs font-medium text-primary hover:underline"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => setRevokeTarget(k)}
                                className="text-xs font-medium text-amber-600 hover:underline"
                              >
                                Revoke
                              </button>
                            </>
                          )}
                          {k.usageCount === 0 && (
                            <button
                              onClick={() => setDeleteTarget(k)}
                              className="text-xs font-medium text-rose-600 hover:underline"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <NewApiKeyDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onCreated={(key) => setCreatedKey(key)}
        />

        {createdKey && (
          <ApiKeyCreatedModal
            open={!!createdKey}
            plainKey={createdKey.plainKey}
            keyName={createdKey.name}
            onClose={() => setCreatedKey(null)}
          />
        )}

        <EditApiKeyDrawer
          apiKey={editTarget}
          onClose={() => setEditTarget(null)}
        />

        <ConfirmDialog
          open={!!revokeTarget}
          onOpenChange={(o) => !o && setRevokeTarget(null)}
          title="Revoke API key?"
          description={
            revokeTarget
              ? `Revoke "${revokeTarget.name}"? Requests using this key will immediately stop working. This cannot be undone.`
              : ""
          }
          onConfirm={async () => {
            if (revokeTarget) await revoke.mutateAsync(revokeTarget.id);
          }}
        />

        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete API key?"
          description={
            deleteTarget
              ? `Permanently delete "${deleteTarget.name}"? Only unused keys can be deleted.`
              : ""
          }
          onConfirm={async () => {
            if (deleteTarget) await del.mutateAsync(deleteTarget.id);
          }}
        />
      </div>
    </AppLayout>
  );
}
